
import React, { Fragment } from 'react';

// Components

import SummaryItem from '../SummaryItem';

// Utils

import { analyzeTaxHouseholdComposition } from '@utils/householdMember/analyzeTaxHouseholdComposition';
import { getMarriageStatus } from '@utils/householdMember/getMarriageStatus';
import { householdMemberTools } from '@utils/tools/householdMemberTools';

// Constants

import { NO_PRIOR_DATA } from '@constants/ssap/miscellaneous/miscellaneous';
import { THC_TYPES } from '@constants/ssap/hhmTypes/taxRelationsTypes';

const HouseholdInfo = props => {

    const {
        idx,
        isState,
        personId,
        relations,
        serverDate,
        taxHousehold,
        taxHousehold: {
            bloodRelationship,
            householdMember: householdMembers,
            primaryTaxFilerPersonId
        },
        taxHouseholdComposition: {
            taxFilingStatus,
            taxRelationship,
            dependentOnExternalFiler,
            claimedByNonCustodialParent,
            claimedByOtherThanSpouseOrParent
        }
    } = props;

    const isMarried = getMarriageStatus(personId, bloodRelationship);

    const getIsFiler = taxRelationship => {
        return taxRelationship === THC_TYPES.FILER || taxRelationship === THC_TYPES.FILER_DEPENDENT;
    };

    const getIsFilingJointly = taxFilingStatus => {
        return taxFilingStatus === THC_TYPES.FILING_JOINTLY;
    };

    const isFiler = getIsFiler(taxRelationship);
    const isFilingJointly = getIsFilingJointly(taxFilingStatus);

    const getDependents = taxHousehold => {
        const family = analyzeTaxHouseholdComposition({
            householdMembers,
            relations,
            bloodRelationship,
            serverDate,
            primaryTaxFilerPersonId,
            isState
        });

        return family
            .dependents
            .filter(d => d.isDependent)
            .reduce((claimedDependents, { claimerIds, dependentFullName }) => {
                return claimerIds.includes(personId)
                    ? [ ...claimedDependents, dependentFullName ]
                    : claimedDependents;
            }, []);
    };

    const getDependentsNames = data => {
        const dependents = getDependents(data);

        return dependents.length > 1
            ? dependents.join(', ')
            : dependents.toString();
    };

    const getPtfName = (taxHousehold, isPrevious = false) => {
        const {
            householdMember,
            primaryTaxFilerPersonId: ptfPersonId
        } = taxHousehold;

        if (ptfPersonId) {
            return householdMemberTools
                .extractFromHouseholdMembers(householdMember)
                .getFullNameByHhmId(ptfPersonId);
        } else {
            return isPrevious ? NO_PRIOR_DATA : null;
        }
    };

    const ptfName = getPtfName(taxHousehold);

    return (
        <Fragment>

            <SummaryItem
                textCode = 'common.planning_to_file'
                value = { isFiler }
                desc = {
                    [
                        [
                            `taxHousehold.householdMember[${ idx }].taxHouseholdComposition.taxRelationship`,
                            getIsFiler
                        ]
                    ]
                }
            />
            {
                isMarried &&
                    <SummaryItem
                        textCode = 'common.household_filing_jointly'
                        value = { isFilingJointly }
                        desc = {
                            [
                                [
                                    `taxHousehold.householdMember[${ idx }].taxHouseholdComposition.taxFilingStatus`,
                                    getIsFilingJointly
                                ]
                            ]
                        }
                    />
            }

            {
                ptfName
                    ? (
                        <SummaryItem
                            textCode = 'common.who_is_ptf'
                            value = { ptfName }
                            ignoreIntl
                            desc = {
                                [
                                    [
                                        [
                                            'DATA.TAX_HOUSEHOLD'
                                        ],
                                        getPtfName,
                                        [ true ]
                                    ]
                                ]
                            }
                        />
                    )
                    : null
            }

            {
                isFiler && !!getDependents(taxHousehold).length &&

                <SummaryItem
                    textCode = {
                        getDependents(taxHousehold).length > 1
                            ? 'common.household_dependants_claimed'
                            : 'common.household_dependant_claimed'
                    }
                    value = { getDependentsNames(taxHousehold) }
                    ignoreIntl
                    desc = {
                        [
                            [
                                [
                                    'DATA.TAX_HOUSEHOLD'
                                ],
                                getDependentsNames
                            ]
                        ]
                    }
                />
            }

            {
                isState.MN
                    ? (
                        <Fragment>
                            {
                                dependentOnExternalFiler !== null &&

                                <SummaryItem
                                    textCode = 'ssap.family_household.tax_composition.is_dependent_on_external_filer'
                                    value = { dependentOnExternalFiler }
                                    noPriorDataValues = { [ null ] }
                                    desc = {
                                        [
                                            [
                                                `taxHousehold.householdMember[${ idx }].taxHouseholdComposition.dependentOnExternalFiler`
                                            ]
                                        ]
                                    }
                                />
                            }

                            {
                                claimedByNonCustodialParent !== null &&

                                <SummaryItem
                                    textCode = 'ssap.family_household.tax_composition.is_claimed_by_non_custodial_parent'
                                    value = { claimedByNonCustodialParent }
                                    noPriorDataValues = { [ null ] }
                                    desc = {
                                        [
                                            [
                                                `taxHousehold.householdMember[${ idx }].taxHouseholdComposition.claimedByNonCustodialParent`
                                            ]
                                        ]
                                    }
                                />
                            }

                            {
                                claimedByOtherThanSpouseOrParent !== null &&

                                <SummaryItem
                                    textCode = 'ssap.family_household.tax_composition.is_claimed_by_other_than_spouse_or_parent'
                                    value = { claimedByOtherThanSpouseOrParent }
                                    noPriorDataValues = { [ null ] }
                                    desc = {
                                        [
                                            [
                                                `taxHousehold.householdMember[${ idx }].taxHouseholdComposition.claimedByOtherThanSpouseOrParent`
                                            ]
                                        ]
                                    }
                                />
                            }
                        </Fragment>
                    )
                    : null
            }
        </Fragment>
    );
};

export default HouseholdInfo;